import BootPay from 'bootpay-js';
import { computed, ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';

import { useOutbound } from '@/lib/_API/useOutbound';
import { usePushCompleteCenter } from '@/lib/outbound/usePushCompleteCenter';
import { useRouter } from 'vue-router';
import { outboundAPI } from '@/apis';
import useFormatNumber from '@/lib/useFormatNumber';
import { state as registryState } from './useMassRegistry';
import { useParcelState } from './useParcel';
import { state as massParcelState } from './useMassParcel';

async function loadBootPay() {
  const BootPay = await import('bootpay-js').then((mod) => mod.default || mod);
  return BootPay;
}

const BOOTPAY_CODE: { [key: string]: string } = {
  master: '607cd2115b2948002707b606',
  development: '60c9a07e5b2948001fdc4911',
  release: '60c9a07e5b2948001fdc4911',
  local: '60c9a07e5b2948001fdc4911',
};

type BootPayRequest = {
  price?: number | string;
  application_id: string;
  name: string;
  pg: string;
  method: string | null;
  show_agree_window: number;
  order_id: string;
  return_url?: string;
};

const { formatRemoveCommas } = useFormatNumber();

/**
 *
 *
 *
 * 결제정보 데이터 모델
 */
export const MODEL_PAYMENT = (): outbound.payment => {
  return cloneDeep({
    id: null,
    type: null,
    agency: '',
    receiptId: '',
    bundleName: '',
  });
};
/**
 *
 *
 * 택배 주문을 결제합니다.
 */
export function usePaymentParcel() {
  const store = useStore();
  const { parcelOrderInfomation } = useParcelState();

  const isPaymentBusyStatus = ref<boolean>(false);
  /**
   * 택배 결제단계 스피너 호출
   */
  const isPaymentBusy = computed({
    get: () => isPaymentBusyStatus.value,
    set: (value) => {
      isPaymentBusyStatus.value = value;
    },
  });
  /**
   * 택배의 결제대기 목록을 불러옵니다.
   */
  const paymentParcelList = computed<Readonly<outbound.parcel.package[]>>(() => store.state.outboundParcel.paymentList);
  /**
   * 택배의 결제대기 목록을 불러옵니다.
   */
  const paymentParcel = computed<Readonly<outbound.payment>>(() => store.state.outboundParcel.payment);
  /**
   * 결제 대기 목록 중, 선택 된 주문이 있는지 확인 합니다.
   */
  const getParcelCheckExist = computed<boolean>(() => {
    for (let i = 0; i < paymentParcelList.value.length; i++) {
      if (paymentParcelList.value[i].orderInfomation.checked) return true;
    }
    return false;
  });
  /**
   * 결제대기 목록중 선택 된것만 가져옵니다.
   */
  const getParcelPaymentCheckedList = computed<Readonly<outbound.parcel.package[]>>(() => {
    const result: outbound.parcel.package[] = [];
    paymentParcelList.value.forEach((el) => {
      if (el.orderInfomation.checked) result.push(el);
    });
    return result;
  });
  /**
   * 선택 된 결제목록의 총금액
   */
  const getParcelCheckedPrice = computed(() => {
    const totalPrice = getParcelPaymentCheckedList.value.reduce((stack, el, i) => {
      return stack + el.fee.totalCharge!;
    }, 0);

    return totalPrice;
  });
  /**
   * 결제가능한 상태인지 확인합니다.
   * true: 불가능, false: 가능
   */
  const getParcelPaymentStatus = computed(() => {
    if (!getParcelCheckExist.value) return true;
    if (!paymentParcel.value.type) return true;
    return false;
  });

  /**
   * 결제모달 띄울수있는 상태인지 확인
   * (기존 페이지이동 -> Outbound 화면 내 모달)
   * true: 불가능, false: 가능
   */
  const getUsePaymentModalStatus = computed(() => {
    return store.state.outboundParcel.canUsePayment;
  });
  /** 택배_결제모달 오픈가능여부 업데이트 */
  const updateCanUsePayment = (value: boolean) => {
    store.dispatch('outboundParcel/updateCanUsePayment', value);
  };

  /**
   * 결제대기목록 업데이트, 배열초기화는 []를 넘겨줍니다.
   */
  const updateParcelPaymentList = (value: outbound.parcel.package[]) => {
    store.dispatch('outboundParcel/updatePaymentList', cloneDeep([...value]));
  };
  /**
   * 결제방법 업데이트
   */
  const updateParcelPayment = <T extends outbound.payment, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundParcel/updatePayment', {
      key: key,
      value: value,
    });
  };
  /**
   * 주문선택 데이터를 업데이트 합니다.
   * 인덱스 넘버를 인자로 주지 않으면 모든 주문을 체크합니다.
   */
  const updateParcelCheck = (checkIndex: number | undefined = undefined) => {
    /**
     * 하나의 list만 업데이트 합니다.
     */
    const _updateSingle = () => {
      const list = paymentParcelList.value;
      list[checkIndex!].orderInfomation.checked = !list[checkIndex!].orderInfomation.checked;

      updateParcelPaymentList([...list]);
    };
    /**
     * 모든 list를 업데이트합니다.
     * 기존에 선택된 주문이 있는경우: 모두선택해제
     * 기존에 선택된 주문이 없는경우: 모두선택
     */
    const _updateAll = () => {
      const isExistCheck = getParcelCheckExist.value;

      updateParcelPaymentList(
        paymentParcelList.value.map((el) => {
          el.orderInfomation.checked = !isExistCheck;
          return el;
        })
      );
    };

    return typeof checkIndex === 'number' ? _updateSingle() : _updateAll();
  };
  /**
   * 결제 대기 목록을 삭제합니다.
   * 인자가 있는 경우 해당 주문 만 삭제하고,
   * 없는 경우 선택 된 주문만 삭제합니다.
   */
  const deleteParcelPaymentList = (selectIndex: number | undefined = undefined) => {
    // 사용자가 단일삭제를 누른 경우 사용
    const _deleteSelected = () => {
      const list = cloneDeep(paymentParcelList.value as outbound.parcel.package[]);

      list.splice(selectIndex!, 1);
      return updateParcelPaymentList(list);
    };
    // 사용자가 선택삭제를 누른 경우 사용
    const _deleteChecked = () => {
      const list = cloneDeep(paymentParcelList.value as outbound.parcel.package[]);

      updateParcelPaymentList(
        list.filter((el) => {
          if (!el.orderInfomation.checked) return el;
        })
      );
    };

    return typeof selectIndex === 'number' ? _deleteSelected() : _deleteChecked();
  };
  /**
   * 결제방법 초기화
   */
  const resetParcelPayment = <T extends outbound.payment, K extends keyof T>() => {
    const key = Object.keys(MODEL_PAYMENT()) as Array<K>;
    const value = Object.values(MODEL_PAYMENT()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateParcelPayment(el, value[index]));
  };

  const getParcelSubmitOrders = computed<api.outbound.parcel.parcelPayloadFormat[]>(() => {
    const result = getParcelPaymentCheckedList.value.map((el) => {
      const order: api.outbound.parcel.parcelPayloadFormat = {
        origin: {
          name: el.origin.name,
          phoneNumber: el.origin.phoneNumber.value,
          roadAddress: el.origin.roadAddress,
          jibunAddress: el.origin.jibunAddress,
          detailAddress: el.origin.detailAddress,
          postalCode: el.origin.postalCode,
          dongName: el.origin.dongName,
          latitude: el.origin.latitude,
          longitude: el.origin.longitude,
          pickupTime: el.origin.pickupTime,
        },
        destination: {
          name: el.destination.name,
          phoneNumber: el.destination.phoneNumber.value,
          roadAddress: el.destination.roadAddress,
          jibunAddress: el.destination.jibunAddress,
          detailAddress: el.destination.detailAddress,
          postalCode: el.destination.postalCode,
          dongName: el.destination.dongName,
          latitude: el.destination.latitude,
          longitude: el.destination.longitude,
          pickupTime: el.destination.pickupTime,
        },
        item: {
          document: 0,
          smallBox: 0,
          bigBox: 0,
          etc: el.item.category,
        },
        itemPrice: el.item.price,
        notifiedPrice: el.fee.totalCharge!,
        chargeUuid: el.fee.chargeUuid,
        pickupFromCenter: el.orderInfomation.pickupFromCenter,
        clientComment: el.orderInfomation.clientComment,
        vehicle: 'bike',
      };

      if (el.orderInfomation.customFields.length) {
        order.customFields = el.orderInfomation.customFields;
      }

      //규격 추가된 택배 접수인경우만 payload 추가
      if (el.item.size._id) {
        order.parcelCategoryId = el.item.size._id;
      }

      return order;
    });

    return result;
  });

  /**
   * 택배_주문요청
   */
  const requestParcelOrder = async (payload: api.outbound.parcel.payload) => {
    const { registerParcel } = useOutbound();

    try {
      store.dispatch('outboundComplete/updateStatusOrderComplete', true);
      const { code, result, popup } = await registerParcel(payload);
      if (code === 13001) return store.dispatch('alert/openAlert', 500);
      if (code === 12005) {
        store.dispatch('alert/openAlert', popup);
        store.dispatch('alert/updateErrorMsg', popup);
        return;
      }
      store.dispatch('outboundComplete/updateCompleteList', [result]);
      usePushCompleteCenter();
    } catch (e) {
      store.dispatch('outboundComplete/updateStatusOrderComplete', false);
    } finally {
    }
  };

  /**
   * 택배_부트페이 결제를 진행합니다.
   */
  const _paymentBootPay = async (isMass?: boolean) => {
    const { $getBundleNumber } = useOutbound();
    const { bundleNumber } = await $getBundleNumber('parcel');
    const BOOTPAY_PRICE = getParcelCheckedPrice.value;
    const BOOTPAY_METHOD = paymentParcel.value.type;

    // 번들번호 없는경우 에러 출력
    if (!bundleNumber) return store.dispatch('alert/openAlert', 500);

    const requestBootPay: BootPayRequest = {
      price: BOOTPAY_PRICE, //실제 결제되는 가격
      application_id: BOOTPAY_CODE[import.meta.env.VITE_ENV],
      name: '디포스트_택배 서비스', //결제창에서 보여질 이름
      pg: 'nicepay',
      method: BOOTPAY_METHOD, //결제수단, 입력하지 않으면 결제수단 선택부터 화면이 시작합니다.
      show_agree_window: 0, // 부트페이 정보 동의 창 보이기 여부
      order_id: bundleNumber, //고유 주문번호로, 생성하신 값을 보내주셔야 합니다.
    };

    BootPay.request(requestBootPay)
      .error((data: any) => {
        store.dispatch('alert/openAlert', 500);

        updateCanUsePayment(false);
      })
      .done((data: any) => {
        const payload: api.outbound.parcel.payload = {
          agency: 'bootpay',
          receiptId: data.receipt_id,
          bundleNumber: bundleNumber,
          paymentMethodId: null,
          paymentMethodType: 'easy',
          outboundParcels: getParcelSubmitOrders.value,
          customerDropOffMethod: isMass
            ? massParcelState.customerDropOffMethod || 'centerDropOff'
            : paymentParcelList.value[0].customerDropOffMethod || 'centerDropOff',
          onsitePickupRequestedAt: isMass
            ? massParcelState.onsitePickupRequestedAt || ''
            : paymentParcelList.value[0].onsitePickupRequestedAt || '',
          onsitePickupClientComment: isMass
            ? massParcelState.onsitePickupClientComment || ''
            : paymentParcelList.value[0].onsitePickupClientComment || '',
        };

        requestParcelOrder(payload);
      });
  };

  /**
   * 택배_선택한 주문 결제
   */
  const submitParcelPayment = async (isMass?: boolean) => {
    const easyPaymentList: outbound.paymentType[] = ['kakao', 'npay', 'payco'];
    const paymentType = paymentParcel.value.type;

    // 카카오, 네이버, 페이코 간편결제
    if (easyPaymentList.includes(paymentType!)) return await _paymentBootPay(isMass);

    // 방문결제, 개인카드결제
    const payload: api.outbound.parcel.payload = {
      agency: '',
      receiptId: '',
      bundleNumber: '',
      paymentMethodId: paymentParcel.value.id,
      paymentMethodType: paymentType as 'cat' | 'billing' | 'credit',
      outboundParcels: getParcelSubmitOrders.value,
      customerDropOffMethod: isMass
        ? massParcelState.customerDropOffMethod || 'centerDropOff'
        : paymentParcelList.value[0].customerDropOffMethod || 'centerDropOff',
      onsitePickupRequestedAt: isMass
        ? massParcelState.onsitePickupRequestedAt || ''
        : paymentParcelList.value[0].onsitePickupRequestedAt || '',
      onsitePickupClientComment: isMass
        ? massParcelState.onsitePickupClientComment || ''
        : paymentParcelList.value[0].onsitePickupClientComment || '',
    };
    requestParcelOrder(payload);
  };

  return {
    paymentParcelList,
    updateParcelPaymentList,
    updateParcelPayment,
    resetParcelPayment,
    getParcelCheckExist,
    updateParcelCheck,
    deleteParcelPaymentList,
    paymentParcel,
    getParcelCheckedPrice,
    getParcelPaymentCheckedList,
    submitParcelPayment,
    getParcelSubmitOrders,
    requestParcelOrder,
    getParcelPaymentStatus,
    isPaymentBusy,
    updateCanUsePayment,
    getUsePaymentModalStatus,
  };
}

/**
 *
 *
 *
 * 퀵 결제합니다.
 */
export function usePaymentQuick() {
  const store = useStore();

  /**
   * 퀵 결제단계 스피너 호출
   */
  const isPaymentBusy = ref<boolean>(false);

  /**
   * 결제대기 목록을 불러옵니다.
   */
  const paymentQuickList = computed<Readonly<outbound.quick.package[]>>(() => store.state.outboundQuick.paymentList);
  /**
   * 결제대기 목록을 불러옵니다.
   */
  const paymentQuick = computed<Readonly<outbound.payment>>(() => store.state.outboundQuick.payment);
  /**
   * 결제 대기 목록 중, 선택 된 주문이 있는지 확인 합니다.
   */
  const getQuickCheckExist = computed<boolean>(() => {
    for (let i = 0; i < paymentQuickList.value.length; i++) {
      if (paymentQuickList.value[i].orderInfomation.checked) return true;
    }
    return false;
  });
  /**
   * 결제대기 목록중 선택 된것만 가져옵니다.
   */
  const getQuickPaymentCheckedList = computed<Readonly<outbound.quick.package[]>>(() => {
    const result: outbound.quick.package[] = [];
    paymentQuickList.value.forEach((el) => {
      if (el.orderInfomation.checked) result.push(el);
    });
    return result;
  });
  /**
   * 선택 된 결제목록의 총금액
   */
  const getQuickCheckedPrice = computed(() => {
    const totalPrice = getQuickPaymentCheckedList.value.reduce((stack, el, i) => {
      return stack + el.fee.totalCharge!;
    }, 0);

    return totalPrice;
  });
  /**
   * 결제모달 띄울수있는 상태인지 확인
   * (기존 페이지이동 -> Outbound 화면 내 모달)
   * true: 불가능, false: 가능
   */
  const getUsePaymentModalStatus = computed(() => {
    return store.state.outboundQuick.canUsePayment;
  });
  /** 퀵_결제모달 오픈가능여부 업데이트 */
  const updateCanUsePayment = (value: boolean) => {
    store.dispatch('outboundQuick/updateCanUsePayment', value);
  };

  /**
   * 결제가능한 상태인지 확인합니다.
   * true: 불가능, false: 가능
   */
  const getQuickPaymentStatus = computed(() => {
    if (!getQuickCheckExist.value) return true;
    if (!paymentQuick.value.type) return true;
    return false;
  });
  /**
   * 결제대기목록 업데이트, 배열초기화는 []를 넘겨줍니다.
   */
  const updateQuickPaymentList = (value: outbound.quick.package[]) => {
    store.dispatch('outboundQuick/updatePaymentList', cloneDeep([...value]));
  };
  /**
   * 결제방법 업데이트
   */
  const updateQuickPayment = <T extends outbound.payment, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundQuick/updatePayment', {
      key: key,
      value: value,
    });
  };
  /**
   * 주문선택 데이터를 업데이트 합니다.
   * 인덱스 넘버를 인자로 주지 않으면 모든 주문을 체크합니다.
   */
  const updateQuickCheck = (checkIndex: number | undefined = undefined) => {
    /**
     * 하나의 list만 업데이트 합니다.
     */
    const _updateSingle = () => {
      const list = paymentQuickList.value;
      list[checkIndex!].orderInfomation.checked = !list[checkIndex!].orderInfomation.checked;

      updateQuickPaymentList([...list]);
    };
    /**
     * 모든 list를 업데이트합니다.
     * 기존에 선택된 주문이 있는경우: 모두선택해제
     * 기존에 선택된 주문이 없는경우: 모두선택
     */
    const _updateAll = () => {
      const isExistCheck = getQuickCheckExist.value;

      updateQuickPaymentList(
        paymentQuickList.value.map((el) => {
          el.orderInfomation.checked = !isExistCheck;
          return el;
        })
      );
    };

    return typeof checkIndex === 'number' ? _updateSingle() : _updateAll();
  };
  /**
   * 결제 대기 목록을 삭제합니다.
   * 인자가 있는 경우 해당 주문 만 삭제하고,
   * 없는 경우 선택 된 주문만 삭제합니다.
   */
  const deleteQuickPaymentList = (selectIndex: number | undefined = undefined) => {
    // 사용자가 단일삭제를 누른 경우 사용
    const _deleteSelected = () => {
      const list = cloneDeep(paymentQuickList.value as outbound.quick.package[]);

      list.splice(selectIndex!, 1);
      return updateQuickPaymentList(list);
    };
    // 사용자가 선택삭제를 누른 경우 사용
    const _deleteChecked = () => {
      const list = cloneDeep(paymentQuickList.value as outbound.quick.package[]);

      updateQuickPaymentList(
        list.filter((el) => {
          if (!el.orderInfomation.checked) return el;
        })
      );
    };

    return typeof selectIndex === 'number' ? _deleteSelected() : _deleteChecked();
  };
  /**
   * 결제방법 초기화
   */
  const resetQuickPayment = <T extends outbound.payment, K extends keyof T>() => {
    const key = Object.keys(MODEL_PAYMENT()) as Array<K>;
    const value = Object.values(MODEL_PAYMENT()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateQuickPayment(el, value[index]));
  };

  const getQuickSubmitOrders = computed<api.outbound.quick.quickPayloadFormat[]>(() => {
    const result = getQuickPaymentCheckedList.value.map((el) => {
      const order: api.outbound.quick.quickPayloadFormat = {
        origin: {
          name: el.origin.name,
          phoneNumber: el.origin.phoneNumber.value,
          roadAddress: el.origin.roadAddress,
          jibunAddress: el.origin.jibunAddress,
          detailAddress: el.origin.detailAddress,
          postalCode: el.origin.postalCode,
          dongName: el.origin.dongName,
          latitude: el.origin.latitude,
          longitude: el.origin.longitude,
          pickupTime: el.origin.pickupTime,
        },
        destination: {
          name: el.destination.name,
          phoneNumber: el.destination.phoneNumber.value,
          roadAddress: el.destination.roadAddress,
          jibunAddress: el.destination.jibunAddress,
          detailAddress: el.destination.detailAddress,
          postalCode: el.destination.postalCode,
          dongName: el.destination.dongName,
          latitude: el.destination.latitude,
          longitude: el.destination.longitude,
          pickupTime: el.destination.pickupTime,
        },
        item: {
          document: el.item.category === '서류' ? el.item.count : 0,
          smallBox: el.item.category === '소박스' ? el.item.count : 0,
          bigBox: el.item.category === '대박스' ? el.item.count : 0,
          //etc + 물품갯수로 파싱
          etc: el.item.category === '기타' ? el.item.etc : '',
        },
        chargeUuid: el.fee.chargeUuid,
        weight: el.item.weight,
        itemPrice: el.item.price,
        notifiedPrice: el.fee.totalCharge!,
        pickupFromCenter: el.orderInfomation.pickupFromCenter,
        clientComment: el.orderInfomation.clientComment,
        vehicle: el.orderInfomation.vehicle,
        isReserved: el.orderInfomation.isReserved,
        isRoundTrip: el.orderInfomation.isRoundTrip,
        isExpress: el.orderInfomation.isExpressQuick,
        customerDropOffMethod: el.orderInfomation.customerDropOffMethod,
        onsitePickupRequestedAt: el.orderInfomation.onsitePickupRequestedAt,
        onsitePickupClientComment: el.orderInfomation.onsitePickupClientComment || '',
      };

      if (el.orderInfomation.customFields.length) {
        order.customFields = el.orderInfomation.customFields;
      }
      return order;
    });

    return result;
  });

  const getQuickFreightSubmitOrders = computed<api.outbound.freightPayloadFormat[]>(() => {
    type vehicle = 'damas' | 'labo' | '1t';
    type vehicleOption =
      | 'cargo'
      | 'canvas'
      | 'boxTruck'
      | 'lowBoxTruck'
      | 'wingbodyTruck'
      | 'freezerTruck'
      | 'refrigeratorTruck'
      | 'liftTruck'
      | 'damasNormal';

    const result = getQuickPaymentCheckedList.value.map((el) => {
      const order: api.outbound.freightPayloadFormat = {
        origin: {
          name: el.origin.name,
          phoneNumber: el.origin.phoneNumber.value,
          roadAddress: el.origin.roadAddress,
          jibunAddress: el.origin.jibunAddress,
          detailAddress: el.origin.detailAddress,
          postalCode: el.origin.postalCode,
          dongName: el.origin.dongName,
          latitude: el.origin.latitude,
          longitude: el.origin.longitude,
          pickupTime: el.origin.pickupTime,
          loadMethod: el.freightItem?.loadMethod,
          loadPosition: el.freightItem?.loadPosition,
        },
        destination: {
          name: el.destination.name,
          phoneNumber: el.destination.phoneNumber.value,
          roadAddress: el.destination.roadAddress,
          jibunAddress: el.destination.jibunAddress,
          detailAddress: el.destination.detailAddress,
          postalCode: el.destination.postalCode,
          dongName: el.destination.dongName,
          latitude: el.destination.latitude,
          longitude: el.destination.longitude,
          pickupTime: el.destination.pickupTime,
          unloadMethod: el.freightItem?.unloadMethod,
          unloadPosition: el.freightItem?.unloadPosition,
        },
        item: {
          etc: el.freightItem?.itemEtc as string,
        },
        chargeUuid: el.fee.chargeUuid,
        weight: 1,
        itemPrice: formatRemoveCommas(String(el.freightItem?.itemPrice)),
        notifiedPrice: el.fee.totalCharge!,
        pickupFromCenter: el.orderInfomation.pickupFromCenter,
        clientComment: el.orderInfomation.clientComment,
        vehicle: el.freightItem?.freightCategory as vehicle,
        vehicleOption: el.freightItem?.freightType as vehicleOption,
        isReserved: el.orderInfomation.isReserved,
        isRoundTrip: el.orderInfomation.isRoundTrip,
        isExpress: el.orderInfomation.isExpressQuick,
        customerDropOffMethod: el.orderInfomation.customerDropOffMethod,
        onsitePickupRequestedAt: el.orderInfomation.onsitePickupRequestedAt,
        onsitePickupClientComment: el.orderInfomation.onsitePickupClientComment || '',
      };

      if (el.orderInfomation.customFields.length) {
        order.customFields = el.orderInfomation.customFields;
      }
      return order;
    });

    return result;
  });

  /**
   * 퀵 - 주문요청
   */
  const requestQuickOrder = async (
    quickType?: string,
    payload?: api.outbound.quick.payload | api.outbound.freightPayload
  ) => {
    const { registerQuick, registerFreightQuick } = useOutbound();

    try {
      store.dispatch('outboundComplete/updateStatusOrderComplete', true);
      const { code, result, popup } =
        quickType === 'bike'
          ? await registerQuick(payload as api.outbound.quick.payload)
          : await registerFreightQuick(payload as api.outbound.freightPayload);

      if (code === 13001) return store.dispatch('alert/openAlert', 500);
      if (code === 12005) {
        store.dispatch('alert/openAlert', popup);
        store.dispatch('alert/updateErrorMsg', popup);
        return;
      }

      store.dispatch('outboundComplete/updateCompleteList', [result]);
      usePushCompleteCenter();
    } catch (e) {
      store.dispatch('outboundComplete/updateStatusOrderComplete', false);
    } finally {
    }
  };

  /**
   * 퀵_부트페이 결제
   */
  const _paymentBootPay = async (quickType?: string, payloadFreight?: api.outbound.freightPayload) => {
    const { $getBundleNumber } = useOutbound();
    const { bundleNumber } = await $getBundleNumber('quick');
    const BOOTPAY_PRICE = getQuickCheckedPrice.value;
    const BOOTPAY_METHOD = paymentQuick.value.type;
    sessionStorage.setItem('preventRefresh', 'false');

    const BootPay = await loadBootPay();
    if (!BootPay || typeof BootPay.request !== 'function') {
      return;
    }

    if (!(window as any).KakaoPay) {
      console.warn('⚠️ KakaoPay SDK가 없음 - 로드 시도');
      const script = document.createElement('script');
      script.src = 'https://developers.kakao.com/sdk/js/kakao.min.js';
      script.onload = async () => {
        await nextTick();
      };
      document.head.appendChild(script);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sessionStorage.setItem('preventRefresh', 'true');
      } else if (document.visibilityState === 'visible') {
        if (sessionStorage.getItem('preventRefresh') === 'true') {
          try {
            history.replaceState(null, '', window.location.href);
          } catch (e) {
            console.warn('⚠️ history.replaceState() 실패, window.location.href 사용');
            setTimeout(() => {
              window.location.href = window.location.href; // iOS에서 강제 URL 변경
            }, 300);
          }
          sessionStorage.setItem('preventRefresh', 'false');
        }
      }
    });

    // 번들번호 없는경우 에러 출력
    if (!bundleNumber) return store.dispatch('alert/openAlert', 500);
    const applicationId = BOOTPAY_CODE[import.meta.env.VITE_ENV];

    await nextTick(); // Vue의 DOM 업데이트가 완료된 후 실행

    const requestBootPay: BootPayRequest = {
      price: BOOTPAY_PRICE, //실제 결제되는 가격
      application_id: applicationId,
      name: '디포스트_퀵 서비스', //결제창에서 보여질 이름
      pg: 'nicepay',
      method: BOOTPAY_METHOD, //결제수단, 입력하지 않으면 결제수단 선택부터 화면이 시작합니다.
      show_agree_window: 0, // 부트페이 정보 동의 창 보이기 여부
      order_id: bundleNumber, //고유 주문번호로, 생성하신 값을 보내주셔야 합니다.
    };

    BootPay.request(requestBootPay)
      .error((data: any) => {
        store.dispatch('alert/openAlert', 500);
        updateCanUsePayment(false);
      })
      .done((response: any) => {
        sessionStorage.setItem('preventRefresh', 'false');
        const payload = {
          agency: 'bootpay',
          receiptId: response.receipt_id,
          bundleNumber: bundleNumber,
          paymentMethodId: null,
          paymentMethodType: 'easy',
          outboundQuicks: !payloadFreight ? getQuickSubmitOrders.value : getQuickFreightSubmitOrders.value,
        };
        requestQuickOrder(quickType, payload as api.outbound.quick.payload | api.outbound.freightPayload);
      })
      .close(() => {
        sessionStorage.setItem('preventRefresh', 'false');
      })
      .then((response: any) => {
        sessionStorage.setItem('receiptId', response.data.receipt_id);
        sessionStorage.setItem('preventRefresh', 'false');
      });
  };

  /**
   * 퀵_선택한 주문 결제
   */
  const submitQuickPayment = async (quickType?: string) => {
    const easyPaymentList: outbound.paymentType[] = ['kakao', 'npay', 'payco'];
    const paymentType = paymentQuick.value.type;

    const payload = {
      agency: '',
      receiptId: '',
      bundleNumber: '',
      paymentMethodId: paymentQuick.value.id,
      paymentMethodType: paymentType as 'cat' | 'billing' | 'credit',
      outboundQuicks: quickType === 'bike' ? getQuickSubmitOrders.value : getQuickFreightSubmitOrders.value,
    };

    // 카카오, 네이버, 페이코 간편결제
    if (easyPaymentList.includes(paymentType!)) {
      const freightPayload = quickType === 'freight' ? (payload as api.outbound.freightPayload) : undefined;
      return await _paymentBootPay(quickType, freightPayload);
    }
    // 일반결제
    requestQuickOrder(quickType, payload as api.outbound.quick.payload | api.outbound.freightPayload);
  };

  return {
    paymentQuickList,
    paymentQuick,
    updateQuickPaymentList,
    updateQuickPayment,
    resetQuickPayment,
    getQuickCheckExist,
    updateQuickCheck,
    deleteQuickPaymentList,
    getQuickCheckedPrice,
    getQuickPaymentCheckedList,
    submitQuickPayment,
    getQuickSubmitOrders,
    getQuickFreightSubmitOrders,
    requestQuickOrder,
    getQuickPaymentStatus,
    getUsePaymentModalStatus,
    updateCanUsePayment,
    isPaymentBusy,
    _paymentBootPay,
  };
}

/**
 *
 * 등기 / 다량등기 접수
 *
 */
export function usePaymentMail() {
  const store = useStore();
  const router = useRouter();
  const { $getBundleNumber, registerMail } = useOutbound();

  /**
   * 결제모달 띄울수있는 상태인지 확인
   * (기존 페이지이동 -> Outbound 화면 내 모달)
   * true: 불가능, false: 가능
   */
  const getUsePaymentModalStatus = computed(() => {
    return store.state.outboundRegistry.canUsePayment;
  });
  /** 등기_결제모달 오픈가능여부 업데이트 */
  const updateCanUsePayment = (value: boolean) => {
    store.dispatch('outboundRegistry/updateCanUsePayment', value);
  };

  /**
   * 등기_부트페이 결제를 진행합니다.
   * @param payload 결제 정보(payload)
   * @param type 등기 타입(registeredMail, expressMail)
   * @param isMass 다량등기 여부(boolean)
   */
  const _paymentBootPay = async (payload: api.outbound.registry.massMailPayload, isMass?: boolean) => {
    const BOOTPAY_PRICE = payload.totalPrice;
    const BOOTPAY_METHOD = payload.paymentMethodType;
    const { bundleNumber } = await $getBundleNumber(payload.category as 'registeredMail' | 'expressMail');
    if (!bundleNumber) return store.dispatch('alert/openAlert', 500);

    const requestBootPay: BootPayRequest = {
      price: BOOTPAY_PRICE, //실제 결제되는 가격
      application_id: BOOTPAY_CODE[import.meta.env.VITE_ENV],
      name: '디포스트_등기 서비스', //결제창에서 보여질 이름
      pg: 'nicepay',
      method: BOOTPAY_METHOD, //결제수단, 입력하지 않으면 결제수단 선택부터 화면이 시작합니다.
      show_agree_window: 0, // 부트페이 정보 동의 창 보이기 여부
      order_id: bundleNumber, //고유 주문번호로, 생성하신 값을 보내주셔야 합니다.
    };

    BootPay.request(requestBootPay)
      .error((data: any) => {
        store.dispatch('alert/openAlert', 500);
      })
      .done(async (data: any) => {
        payload.agency = 'bootpay';
        payload.receiptId = data.receipt_id;
        payload.bundleNumber = bundleNumber;
        payload.paymentMethodId = null;
        payload.paymentMethodType = 'easy';

        return isMass ? await requestMassMailOrder(payload) : await requestMailOrder(payload);
      });
  };

  const submitMailPayment = async (payload: api.outbound.registry.massMailPayload) => {
    const easyPaymentList: outbound.paymentType[] = ['kakao', 'npay', 'payco'];
    const paymentType = payload.paymentMethodType;

    // 카카오, 네이버, 페이코 간편결제
    if (easyPaymentList.includes(paymentType!)) return await _paymentBootPay(payload);
    else {
      const mailPayload: api.outbound.registry.massMailPayload = {
        ...payload,
        agency: '',
        receiptId: '',
        bundleNumber: '',
        paymentMethodType: paymentType as 'cat' | 'billing' | 'credit',
      };

      await requestMailOrder(mailPayload);
    }
  };

  const requestMailOrder = async (payload: api.outbound.registry.massMailPayload) => {
    try {
      store.dispatch('outboundComplete/updateStatusOrderComplete', true);
      const { code, result, popup } = await registerMail(payload);
      await pushMailComplete(code, result, popup!);
    } catch (e) {
      store.dispatch('outboundComplete/updateStatusOrderComplete', false);
      console.error(e);
    }
  };

  const submitMassMailPayment = async (payload: api.outbound.registry.massMailPayload) => {
    const easyPaymentList: outbound.paymentType[] = ['kakao', 'npay', 'payco'];
    const paymentType = payload.paymentMethodType;

    // 카카오, 네이버, 페이코 간편결제
    if (easyPaymentList.includes(paymentType!)) return await _paymentBootPay(payload, true);
    else {
      const mailPayload: api.outbound.registry.massMailPayload = {
        ...payload,
        agency: '',
        receiptId: '',
        bundleNumber: '',
        paymentMethodType: paymentType as 'cat' | 'billing' | 'credit',
      };
      await requestMassMailOrder(mailPayload);
    }
  };

  const requestMassMailOrder = async (payload: api.outbound.registry.massMailPayload) => {
    try {
      store.dispatch('outboundComplete/updateStatusOrderComplete', true);
      const { code, result, popup } = await registerMail(payload);
      await pushMailComplete(code, result, popup!, true);
    } catch (e) {
      store.dispatch('outboundComplete/updateStatusOrderComplete', false);
      console.error(e);
    }
  };

  const pushMailComplete = async (code: number, result: any, popup: string, isMass?: boolean) => {
    if (code === 200) {
      result.isMass = isMass;
      store.dispatch('outboundComplete/updateCompleteList', result);
      await router.push('/outbound/complete');
    } else {
      console.error(result, code);
      alert(popup);
    }
  };

  /**
   * 결제방법 업데이트
   */
  const updateRegistryPayment = <T extends outbound.payment, K extends keyof T>(key: K, value: T[K]) => {
    store.dispatch('outboundRegistry/updatePayment', {
      key: key,
      value: value,
    });
  };
  const paymentRegistry = computed<Readonly<outbound.payment>>(() => store.state.outboundRegistry.payment);
  /**
   * 결제하기 페이로드 세팅
   */
  const setRegistryPayload = (category?: string, isUniformCharge?: boolean): api.outbound.registry.massMailPayload => {
    type OldPayMethodType = 'credit' | 'billing' | 'npay' | 'kakao' | 'payco' | 'groupBilling' | 'cat';

    const sender = store.state.outboundRegistry.registrySender;
    const receiver = store.state.outboundRegistry.registryReceiver;
    const receivers = store.state.outboundRegistry.registryReceivers;
    const customFields = store.state.outboundRegistry.customFields;
    const clientComment = store.state.outboundRegistry.registryClientComment;
    const charge = store.state.outboundRegistry.mailCharge;
    const registryCharge = store.state.outboundRegistry.mailCharge.charge;
    const registryType = computed<outbound.registry.type>(() => {
      return store.getters['outboundRegistry/getRegistryType']();
    });
    const customerDropOffMethod = store.state.outboundRegistry.customerDropOffMethod;
    const onsitePickupRequestedAt = store.state.outboundRegistry.onsitePickupRequestedAt;
    const onsitePickupClientComment = store.state.outboundRegistry.onsitePickupClientComment || '';
    delete receiver.phoneValidate;

    const payload: api.outbound.registry.massMailPayload = {
      paymentMethodType: paymentRegistry.value.type as OldPayMethodType,
      paymentMethodId: paymentRegistry.value.id,
      customerDropOffMethod: customerDropOffMethod || 'centerDropOff',
      onsitePickupRequestedAt: onsitePickupRequestedAt,
      onsitePickupClientComment: onsitePickupClientComment,
    };
    let mailsInfo: api.outbound.registry.massMailInfo[] = [];
    if (category !== 'massRegistry') {
      mailsInfo.push({
        origin: sender,
        destination: receiver,
        clientComment: clientComment ?? '',
        notifiedPrice: isUniformCharge ? charge.charge : 0,
        chargeUuid: isUniformCharge ? charge.chargeUuid : undefined,
        customFields: customFields.length ? customFields : undefined,
      });
    } else {
      receivers.forEach((receiver: outbound.registry.orderList, index: number) => {
        const destination = {
          name: receiver.name.value,
          phoneNumber: receiver.phoneNumber.value,
          jibunAddress: receiver.address.value,
          detailAddress: receiver.detailAddress.value,
          dongName: receiver.dongName,
          postalCode: receiver.postalCode,
          latitude: receiver.latitude,
          longitude: receiver.longitude,
        };
        mailsInfo.push({
          origin: sender,
          destination: destination as api.outbound.registry.mailsPin,
          clientComment: receiver.comment ?? '',
          notifiedPrice: isUniformCharge ? registryState.list[index].totalCharge : 0,
          chargeUuid: isUniformCharge ? registryState.list[index].chargeUuid : undefined,
          customFields: customFields.length ? customFields : undefined,
        });
      });
    }

    payload.category = registryType.value;
    payload.mails = mailsInfo;
    payload.isUniformCharge = isUniformCharge;
    payload.totalPrice = isUniformCharge ? registryCharge : 0;

    return payload;
  };

  return {
    submitMailPayment,
    submitMassMailPayment,
    requestMailOrder,
    requestMassMailOrder,
    getUsePaymentModalStatus,
    updateCanUsePayment,
    updateRegistryPayment,
    setRegistryPayload,
  };
}

/**
 * 국제우편 결제 (떨어져있던 함수 점차 모을 예정)
 */
export function usePaymentGlobal() {
  const store = useStore();
  const router = useRouter();
  const apis = new outboundAPI();

  /**
   * 결제하기
   */
  const submitGlobalPayment = async (payload: api.outbound.globalMailPayload, mailType: string) => {
    try {
      store.dispatch('outboundComplete/updateStatusOrderComplete', true);

      switch (mailType) {
        case 'dhl':
          const dataDhl = await apis.outboundDhl(payload as api.outbound.globalMailPayload);
          if (dataDhl.code === 200) {
            store.dispatch('outboundGlobalMail/updateEmsResponse', dataDhl.result);
            router.push('/outbound/complete');
          }
          break;
        case 'ems':
          const dataEms = await apis.outboundEms(payload as api.outbound.globalMailPayload);
          if (dataEms.code === 200) {
            store.dispatch('outboundGlobalMail/updateEmsResponse', dataEms.result);
            router.push('/outbound/complete');
          }
          break;
        case 'fedex':
          const dataFedex = await apis.outboundFedex(payload as api.outbound.globalMailPayload);
          if (dataFedex.code === 200) {
            store.dispatch('outboundGlobalMail/updateEmsResponse', dataFedex.result);
            router.push('/outbound/complete');
          }
          break;
        case 'ups':
          const dataUps = await apis.outboundUps(payload as api.outbound.globalMailPayload);
          if (dataUps.code === 200) {
            store.dispatch('outboundGlobalMail/updateEmsResponse', dataUps.result);
            router.push('/outbound/complete');
          }
          break;
      }
    } catch (e) {
      store.dispatch('outboundComplete/updateStatusOrderComplete', false);
      console.error(e);
    }
  };

  return { submitGlobalPayment };
}
